// SAD-498 (SOL-02): flexible solution components. Tasks, PP references and
// capabilities are linked by canonical id, never copied; the server resolves
// their current titles on read.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { Choice, lines, options, TextField } from '../PastPerformance/Fields';
import { itemKindLabel } from './SolutionItems';
const COMPONENT_KINDS = [
    'technical',
    'operational',
    'management',
    'transition'
];
const REF_KINDS = [
    'task',
    'pp_reference',
    'capability',
    'staffing',
    'evidence',
    'teammate'
];
const CANONICAL = new Set([
    'task',
    'pp_reference',
    'capability'
]);
const blank = {
    id: '',
    kind: 'technical',
    name: '',
    purpose: '',
    proposedApproach: '',
    needsAddressed: [],
    provider: '',
    openQuestions: [],
    refs: []
};
const blankRef = {
    refKind: 'task',
    refId: '',
    label: ''
};
export function ComponentRow({ c, items }) {
    const need = (id)=>items.find((i)=>i.id === id)?.text ?? id;
    return /*#__PURE__*/ _jsx("li", {
        tabIndex: -1,
        "data-target-id": c.id,
        "data-target-field": "proposed_approach",
        "data-testid": Testids.captureSolutionReview.affected,
        className: "space-y-1 rounded-md border p-2",
        children: /*#__PURE__*/ _jsxs("div", {
            "data-testid": Testids.captureSolution.compRow,
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-wrap items-center gap-2 text-sm",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraBadge, {
                            children: c.kind
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            className: "font-medium",
                            children: c.name
                        }),
                        c.provider && /*#__PURE__*/ _jsxs("span", {
                            children: [
                                "Provider: ",
                                c.provider
                            ]
                        })
                    ]
                }),
                c.purpose && /*#__PURE__*/ _jsxs("div", {
                    className: "text-sm",
                    children: [
                        "Purpose: ",
                        c.purpose
                    ]
                }),
                c.proposedApproach && /*#__PURE__*/ _jsxs("div", {
                    className: "text-sm",
                    children: [
                        "Approach: ",
                        c.proposedApproach
                    ]
                }),
                c.needsAddressed.length > 0 && /*#__PURE__*/ _jsxs("div", {
                    className: "text-sm",
                    children: [
                        "Addresses: ",
                        c.needsAddressed.map(need).join('; ')
                    ]
                }),
                c.openQuestions.length > 0 && /*#__PURE__*/ _jsxs("div", {
                    className: "text-sm",
                    children: [
                        "Open questions: ",
                        c.openQuestions.join('; ')
                    ]
                }),
                /*#__PURE__*/ _jsx("ul", {
                    className: "text-xs",
                    children: c.refs.map((r, i)=>/*#__PURE__*/ _jsxs("li", {
                            "data-testid": Testids.captureSolution.compRef,
                            children: [
                                r.refKind.replace(/_/g, ' '),
                                ": ",
                                r.label || r.refId
                            ]
                        }, `${r.refKind}-${r.refId}-${i}`))
                })
            ]
        })
    });
}
export function ComponentsReadOnly({ components, items }) {
    return /*#__PURE__*/ _jsx("ul", {
        className: "space-y-2",
        children: components.map((c)=>/*#__PURE__*/ _jsx(ComponentRow, {
                c: c,
                items: items
            }, c.id))
    });
}
export function ComponentsEditor({ components, items, onChange }) {
    const [draft, setDraft] = useState(blank);
    const [questions, setQuestions] = useState('');
    const [ref, setRef] = useState(blankRef);
    const patch = (p)=>setDraft((d)=>({
                ...d,
                ...p
            }));
    const canonical = CANONICAL.has(ref.refKind);
    const canAddRef = canonical ? ref.refId.trim() !== '' : ref.label.trim() !== '';
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-2 rounded-md border p-3",
        children: [
            /*#__PURE__*/ _jsx("h3", {
                className: "font-semibold",
                children: "Components"
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-sm",
                children: "Technical, operational, management or transition. Link tasks, PP references and capabilities by record id; they are not copied here."
            }),
            /*#__PURE__*/ _jsx(ComponentsReadOnly, {
                components: components,
                items: items
            }),
            /*#__PURE__*/ _jsx(Choice, {
                label: "Component type",
                testId: Testids.captureSolution.compKind,
                value: draft.kind,
                options: options(COMPONENT_KINDS),
                onChange: (kind)=>patch({
                        kind
                    })
            }),
            /*#__PURE__*/ _jsx(TextField, {
                label: "Component name",
                testId: Testids.captureSolution.compName,
                value: draft.name,
                onChange: (name)=>patch({
                        name
                    })
            }),
            /*#__PURE__*/ _jsx(TextField, {
                label: "Purpose",
                testId: Testids.captureSolution.compPurpose,
                value: draft.purpose,
                onChange: (purpose)=>patch({
                        purpose
                    })
            }),
            /*#__PURE__*/ _jsx(TextField, {
                label: "Proposed approach",
                multiline: true,
                testId: Testids.captureSolution.compApproach,
                value: draft.proposedApproach,
                onChange: (proposedApproach)=>patch({
                        proposedApproach
                    })
            }),
            /*#__PURE__*/ _jsx(TextField, {
                label: "Provider",
                testId: Testids.captureSolution.compProvider,
                value: draft.provider,
                onChange: (provider)=>patch({
                        provider
                    })
            }),
            /*#__PURE__*/ _jsx(TextField, {
                label: "Open questions (one per line)",
                multiline: true,
                testId: Testids.captureSolution.compQuestions,
                value: questions,
                onChange: setQuestions
            }),
            items.length > 0 && /*#__PURE__*/ _jsx("p", {
                className: "text-sm",
                children: "Needs addressed"
            }),
            items.map((i)=>/*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": Testids.captureSolution.compNeed,
                    "aria-pressed": draft.needsAddressed.includes(i.id),
                    onClick: ()=>patch({
                            needsAddressed: draft.needsAddressed.includes(i.id) ? draft.needsAddressed.filter((n)=>n !== i.id) : [
                                ...draft.needsAddressed,
                                i.id
                            ]
                        }),
                    children: `${draft.needsAddressed.includes(i.id) ? '✓ ' : ''}${itemKindLabel(i.kind)}: ${i.text}`
                }, i.id)),
            /*#__PURE__*/ _jsx("ul", {
                className: "text-xs",
                children: draft.refs.map((r, i)=>/*#__PURE__*/ _jsxs("li", {
                        children: [
                            r.refKind.replace(/_/g, ' '),
                            ": ",
                            r.label || r.refId
                        ]
                    }, `${r.refKind}-${r.refId}-${i}`))
            }),
            /*#__PURE__*/ _jsx(Choice, {
                label: "Link type",
                testId: Testids.captureSolution.compRefKind,
                value: ref.refKind,
                options: options(REF_KINDS),
                onChange: (refKind)=>setRef({
                        ...blankRef,
                        refKind
                    })
            }),
            canonical ? /*#__PURE__*/ _jsx(TextField, {
                label: "Record id",
                testId: Testids.captureSolution.compRefId,
                value: ref.refId,
                onChange: (refId)=>setRef((r)=>({
                            ...r,
                            refId
                        }))
            }) : /*#__PURE__*/ _jsx(TextField, {
                label: "Label",
                testId: Testids.captureSolution.compRefLabel,
                value: ref.label,
                onChange: (label)=>setRef((r)=>({
                            ...r,
                            label
                        }))
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.captureSolution.compRefAdd,
                disabled: !canAddRef,
                onClick: ()=>{
                    patch({
                        refs: [
                            ...draft.refs,
                            ref
                        ]
                    });
                    setRef(blankRef);
                },
                children: "Add link"
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.captureSolution.compAdd,
                disabled: !draft.name.trim(),
                onClick: ()=>{
                    onChange([
                        ...components,
                        {
                            ...draft,
                            id: crypto.randomUUID(),
                            openQuestions: lines(questions)
                        }
                    ]);
                    setDraft(blank);
                    setQuestions('');
                },
                children: "Add component"
            })
        ]
    });
}
